// src/auth.js
import { useState, useEffect, useRef } from "react";
import AWS from "aws-sdk";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
} from "firebase/auth";
import { initializeApp } from "firebase/app";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase app (singleton)
let app;
try {
  app = initializeApp(firebaseConfig);
} catch (err) {
  // ignore app already initialized
}

// ─── AWS Cognito configuration ───────────────────────────────────────────────
const IDENTITY_POOL_ID = process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID;
const AWS_REGION = "us-east-1";
const LOGIN_KEY = `securetoken.google.com/${process.env.REACT_APP_FIREBASE_PROJECT_ID}`;

const auth = getAuth(app);
const provider = new GoogleAuthProvider();

/**
 * Trigger Google Sign-In popup
 */
export async function signInWithGoogle() {
  try {
    const { user } = await signInWithPopup(auth, provider);

    // Get a fresh Firebase ID token
    const idToken = await user.getIdToken(true);

    // Install Cognito Identity creds tied to that token
    AWS.config.update({ region: AWS_REGION });
    AWS.config.credentials = new AWS.CognitoIdentityCredentials(
      {
        IdentityPoolId: IDENTITY_POOL_ID,
        Logins: { [LOGIN_KEY]: idToken }, // e.g. "accounts.google.com" or `securetoken.google.com/${FIREBASE_PROJECT_ID}`
      },
      { region: AWS_REGION },
    );

    // Optional but helpful: avoid reusing a cached identityId
    if (typeof AWS.config.credentials.clearCachedId === "function") {
      try {
        AWS.config.credentials.clearCachedId();
      } catch {}
    }

    // Block until STS keys are ready
    await new Promise((resolve, reject) =>
      AWS.config.credentials.get((err) => (err ? reject(err) : resolve())),
    );

    // Now creds are fresh and ready
    return { user, creds: AWS.config.credentials };
  } catch (error) {
    console.error("Google sign-in error", error);
    throw error;
  }
}

/**
 * Custom hook to subscribe to auth state
 * @returns {import('firebase/auth').User | null}
 */

export function useAuth() {
  const auth = getAuth();
  const [user, setUser] = useState(() => auth.currentUser);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        try {
          // 1) Grab a fresh Firebase ID token
          const idToken = await firebaseUser.getIdToken(true);

          // 2) Configure AWS SDK
          AWS.config.update({ region: AWS_REGION });
          AWS.config.credentials = new AWS.CognitoIdentityCredentials({
            IdentityPoolId: IDENTITY_POOL_ID,
            Logins: { [LOGIN_KEY]: idToken },
          });

          // 3) Wait for Cognito credentials to be ready
          await new Promise((resolve, reject) => {
            AWS.config.credentials.get((err) =>
              err ? reject(err) : resolve(),
            );
          });

          console.log(
            "[useAuth] AWS credentials ready, identityId:",
            AWS.config.credentials.identityId,
          );
        } catch (err) {
          console.error("[useAuth] Failed to set AWS credentials:", err);
        }
      } else {
        // Clear AWS creds on sign-out
        AWS.config.credentials = null;
      }
    });

    return unsubscribe;
  }, []);

  return user;
}

export function handleLogout(user) {
  try {
    console.log("Logging out user:", user?.email);
    auth.signOut();
  } catch (err) {
    console.error("Logout failed:", err);
  }
}

export function useAuthWait(onReady) {
  const auth = getAuth();
  const [user, setUser] = useState(() => auth.currentUser);
  const [loading, setLoading] = useState(true);
  const hasCalled = useRef(false); // avoid double calls in React Strict Mode

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        try {
          const idToken = await firebaseUser.getIdToken(true);
          AWS.config.update({ region: AWS_REGION });
          AWS.config.credentials = new AWS.CognitoIdentityCredentials({
            IdentityPoolId: IDENTITY_POOL_ID,
            Logins: { [LOGIN_KEY]: idToken },
          });

          await new Promise((resolve, reject) => {
            AWS.config.credentials.get((err) =>
              err ? reject(err) : resolve(),
            );
          });

          console.log(
            "[useAuthWait] AWS credentials ready, identityId:",
            AWS.config.credentials.identityId,
          );

          if (onReady && !hasCalled.current) {
            hasCalled.current = true;
            onReady(firebaseUser, AWS.config.credentials);
          }
        } catch (err) {
          console.error("[useAuthWait] AWS credential setup failed:", err);
        }
      } else {
        AWS.config.credentials = null;
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  return { user, loading };
}
